/**
 * 13332482513 — "the system automatically deletes texts, throughout the app".
 *
 * Every customer text input keeps a local copy of what the rep typed and
 * saves it to Firebase. Redux only learns about that save from the database
 * echo, and the echo is never on time: `createCustomerChannel` batches it
 * behind a 500 ms debounce, `createCustomersChannel` behind 1000 ms, and on
 * the phone the native SDK hands it to JS asynchronously, after the JS timer
 * that closes the batch may already have fired. So a batch can close holding
 * the value of an OLDER keystroke while a newer one is in flight, and the
 * input copied that older value over what the rep had typed since.
 *
 * The single rule every input follows: while a field has edits the rep made
 * (unsent, or sent within the last `EDIT_ECHO_SETTLE_MS`), a value coming
 * from the database that is the echo of one of those edits, or of the value
 * the field held when the edits began, never replaces the input. Once the
 * edits settle, or a genuinely different value arrives from elsewhere, the
 * input follows the database again exactly as before.
 */
import { parseFloating } from '../utils/utils'

/**
 * Longest the echo of a save can plausibly take to come back: 1000 ms list
 * debounce + native bridge latency, with room to spare on a busy phone.
 */
export const EDIT_ECHO_SETTLE_MS = 3000

/** Bound on how many older values a long typing burst remembers. */
const MAX_OLDER = 500

export interface EditEchoGuard {
  /** Earlier values of this burst, oldest first (incl. the starting value). */
  older: unknown[]
  latest: unknown
  hasLatest: boolean
  /** A debounced edit exists that has not been handed to updateCustomer. */
  unsent: boolean
  lastEditAt: number
}

export const createEditEchoGuard = (): EditEchoGuard => ({
  hasLatest: false,
  lastEditAt: Number.NEGATIVE_INFINITY,
  latest: undefined,
  older: [],
  unsent: false,
})

const burstActive = (g: EditEchoGuard, now: number): boolean =>
  g.hasLatest && (g.unsent || now - g.lastEditAt < EDIT_ECHO_SETTLE_MS)

/**
 * Whether `remote` is what the database gives back for a save of `sent`.
 * Saves trim strings (`api._updateCustomer`) and number fields come back
 * through `sanitizeNumber`, so "Charles " returns as "Charles" and "12."
 * as 12.
 */
export const isEchoOfSave = (sent: unknown, remote: unknown): boolean => {
  if (Object.is(sent, remote)) return true
  if (typeof sent === 'string' && typeof remote === 'string') {
    return sent.trim() === remote.trim()
  }
  if (
    typeof remote === 'number' &&
    (typeof sent === 'string' || typeof sent === 'number')
  ) {
    return parseFloating(sent) === remote
  }
  return false
}

/**
 * Record an edit the rep made. `remoteNow` is what the store holds at that
 * moment; it starts a new burst's memory so its late echo cannot undo the
 * whole burst. `sent` = the value is being handed to updateCustomer now.
 */
export const noteLocalEdit = (
  g: EditEchoGuard,
  value: unknown,
  remoteNow: unknown,
  now: number,
  sent: boolean,
): void => {
  if (!burstActive(g, now)) {
    g.older = [remoteNow]
  } else if (g.hasLatest) {
    g.older.push(g.latest)
    if (g.older.length > MAX_OLDER) g.older.splice(1, 1)
  }
  g.latest = value
  g.hasLatest = true
  g.unsent = !sent
  g.lastEditAt = now
}

/** A debounced edit was just handed to updateCustomer. */
export const noteEditSent = (g: EditEchoGuard, now: number): void => {
  g.unsent = false
  g.lastEditAt = now
}

const resetGuard = (g: EditEchoGuard): void => {
  g.older = []
  g.latest = undefined
  g.hasLatest = false
  g.unsent = false
  g.lastEditAt = Number.NEGATIVE_INFINITY
}

/**
 * Decide whether a new value from the store may replace the input. Returns
 * false for an unsent edit and for any echo of this burst; true (and forgets
 * the burst) for a value from elsewhere or once the burst has settled.
 */
export const shouldTakeRemote = (
  g: EditEchoGuard,
  remote: unknown,
  now: number,
): boolean => {
  if (!g.hasLatest) return true
  if (g.unsent) return false
  if (!burstActive(g, now)) {
    resetGuard(g)
    return true
  }
  if (isEchoOfSave(g.latest, remote)) return false
  if (g.older.some((o) => isEchoOfSave(o, remote))) return false
  resetGuard(g)
  return true
}
